import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { CapacitorUpdater } from '@capgo/capacitor-updater';

const PENDING_KEY = 'laeebly.ota.pending.v2';
const ATTEMPTED_KEY = 'laeebly.ota.attempted.v2';
const IDENTITY_FIELDS = ['platform', 'appId', 'nativeVersion', 'nativeBuild'];
const INSTALLABLE_STATUSES = new Set(['pending', 'success']);
// The startup loader covers the app for at most this long after the first
// healthy route. A slower check or download continues in the background and
// the verified bundle installs on the next launch instead (2026-09-23: the
// loader had no upper bound, so every launch waited for a full ~16MB download).
export const STARTUP_GATE_MS = 8000;
let preparation;
let acknowledgement;
let resolveStartupReady;
let gateReleased = false;
let switching = false;
const startupReady = new Promise((resolve) => {
  resolveStartupReady = resolve;
});

const available = () =>
  Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('CapacitorUpdater');

/**
 * Resolves after the first healthy route has acknowledged the current bundle
 * and the startup OTA check has finished, or after STARTUP_GATE_MS at the
 * latest. Web and old native builds without the updater resolve immediately
 * so they never show the native-only gate.
 */
export function waitForLiveUpdateReady() {
  return available() ? startupReady : Promise.resolve();
}

function nativeVersionCore(version) {
  const match = /^(\d+)\.(\d+)(?:\.(\d+))?(?:[+-].*)?$/.exec(version || '');
  return match ? `${Number(match[1])}.${Number(match[2])}.${Number(match[3] || 0)}` : null;
}

function isCompatible(version, native) {
  const core = nativeVersionCore(version);
  return core !== null && core === nativeVersionCore(native);
}

const nonEmptyString = (value) => typeof value === 'string' && value.length > 0 && value === value.trim();

function sameIdentity(record, identity) {
  return record && IDENTITY_FIELDS.every((field) => record[field] === identity[field]);
}

async function getNativeIdentity(current) {
  const info = await App.getInfo();
  const identity = {
    platform: Capacitor.getPlatform(),
    appId: info.id,
    nativeVersion: info.version,
    nativeBuild: info.build,
  };
  if (!['ios', 'android'].includes(identity.platform) ||
      !IDENTITY_FIELDS.every((field) => nonEmptyString(identity[field])) ||
      !isCompatible(current.native, identity.nativeVersion)) {
    throw new Error('Native update identity unavailable or inconsistent');
  }
  return identity;
}

function removeLegacyRecords() {
  // v1 was never released; its records did not bind to the actual native build.
  window.localStorage.removeItem('laeebly.ota.pending.v1');
  window.localStorage.removeItem('laeebly.ota.attempted.v1');
}

function readRecord(key) {
  const raw = window.localStorage.getItem(key);
  if (!raw) return null;
  try {
    const record = JSON.parse(raw);
    if (nonEmptyString(record.id) && nonEmptyString(record.version) &&
        IDENTITY_FIELDS.every((field) => nonEmptyString(record[field]))) return record;
  } catch {
    // A damaged update marker must never prevent the existing app from opening.
  }
  window.localStorage.removeItem(key);
  return null;
}

function getUpdateOrigin() {
  const configured = new URL(process.env.REACT_APP_SUPABASE_URL);
  if (configured.protocol !== 'https:' || configured.username || configured.password ||
      configured.pathname !== '/' || configured.search || configured.hash) {
    throw new Error('Invalid mobile update service URL');
  }
  return configured.origin;
}

function isAllowedDownloadUrl(value, origin) {
  try {
    const url = new URL(value);
    const path = decodeURIComponent(url.pathname);
    const prefix = '/storage/v1/object/sign/mobile-updates/';
    return url.protocol === 'https:' && url.origin === origin && !url.username && !url.password &&
      !url.hash && path.startsWith(prefix) && path.length > prefix.length &&
      !path.includes('\\') && !path.split('/').some((part) => part === '.' || part === '..') &&
      Boolean(url.searchParams.get('token'));
  } catch {
    return false;
  }
}

async function fetchLatest(identity, version, origin) {
  const controller = new AbortController();
  let timer;
  try {
    // The public Edge Function receives app/build metadata only. No user session,
    // anon key, cookies, or Supabase auth client is involved.
    const response = (async () => {
      const result = await fetch(`${origin}/functions/v1/mobile-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...identity, version }),
        credentials: 'omit',
        cache: 'no-store',
        redirect: 'error',
        signal: controller.signal,
      });
      if (!result.ok) throw new Error(`Mobile update service returned HTTP ${result.status}`);
      return result.json();
    })();
    return await Promise.race([
      response,
      new Promise((_, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new Error('Mobile update request timed out'));
        }, 10000);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

function warn(error) {
  console.warn('[Live updates] Keeping the current app:', error);
}

// Only reads are timed out: a late read cannot replace a page after it is shown.
function readBeforeStartup(promise) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error('Update startup check timed out')), 2000);
    }),
  ]).finally(() => clearTimeout(timer));
}

/**
 * Called once before the application renders. Returns false when a previously
 * downloaded bundle switch has started. New downloads are triggered only after
 * a route has committed successfully, while the startup gate keeps the UI
 * covered until the verified bundle is applied.
 */
export function prepareLiveUpdates() {
  if (!preparation) preparation = preparePendingBundle();
  return preparation;
}

async function preparePendingBundle() {
  try {
    if (!available()) return true;
    removeLegacyRecords();
    const pending = readRecord(PENDING_KEY);
    if (!pending) return true;

    const [{ current, identity }, { bundles }] = await readBeforeStartup(Promise.all([
      CapacitorUpdater.current().then(async (current) => ({ current, identity: await getNativeIdentity(current) })),
      CapacitorUpdater.list(),
    ]));
    const candidate = bundles.find((bundle) => bundle.id === pending.id);
    const attempted = readRecord(ATTEMPTED_KEY);
    const wasAttempted = sameIdentity(attempted, identity) && attempted.version === pending.version;

    // Remove before set(): a broken bundle must not be reinstalled after rollback.
    window.localStorage.removeItem(PENDING_KEY);
    if (!sameIdentity(pending, identity) || !isCompatible(pending.version, identity.nativeVersion) || wasAttempted ||
        current.bundle.id === pending.id || current.bundle.version === pending.version || !candidate ||
        candidate.version !== pending.version || !INSTALLABLE_STATUSES.has(candidate.status)) {
      return true;
    }
    window.localStorage.setItem(ATTEMPTED_KEY, JSON.stringify(pending));
    await CapacitorUpdater.set({ id: pending.id });
    // set() replaces the entire JavaScript context. Do not render the old app.
    return false;
  } catch (error) {
    warn(error);
    return true;
  }
}

/** Confirm the first successfully committed route, then download and apply. */
export function acknowledgeLiveUpdateReady() {
  if (!acknowledgement) {
    // Bounded loader: once released, the user is in the app and a downloaded
    // bundle must wait for the next launch. A switch already in progress keeps
    // the loader until the WebView reloads into the new bundle.
    const deadline = setTimeout(() => {
      gateReleased = true;
      if (!switching) resolveStartupReady();
    }, STARTUP_GATE_MS);
    acknowledgement = acknowledgeAndDownload().finally(() => {
      clearTimeout(deadline);
      gateReleased = true;
      resolveStartupReady();
    });
  }
  return acknowledgement;
}

async function acknowledgeAndDownload() {
  try {
    if (!available()) return;
    await CapacitorUpdater.notifyAppReady();
    const current = await CapacitorUpdater.current();
    const identity = await getNativeIdentity(current);
    removeLegacyRecords();
    const attempted = readRecord(ATTEMPTED_KEY);
    if (attempted && (!sameIdentity(attempted, identity) || attempted.version === current.bundle.version)) {
      window.localStorage.removeItem(ATTEMPTED_KEY);
    }
    await downloadAndApply(current, identity);
  } catch (error) {
    // Network/storage/plugin failures do not block login, navigation, or forms.
    warn(error);
  }
}

async function downloadAndApply(current, identity) {
  const origin = getUpdateOrigin();
  const latest = await fetchLatest(identity, current.bundle.version, origin);
  const failed = readRecord(ATTEMPTED_KEY);
  if (!latest || latest.error || latest.kind || latest.breaking || latest.major || !latest.version || !latest.url ||
      !sameIdentity(latest, identity) || !isCompatible(latest.version, identity.nativeVersion) ||
      latest.version === current.bundle.version ||
      (sameIdentity(failed, identity) && failed.version === latest.version)) return;

  // With updater v6, publicKey alone does not reject unsigned ZIP responses on
  // Android. Require encrypted/signed ZIP metadata as well; delta is not enabled.
  if (typeof latest.sessionKey !== 'string' || !latest.sessionKey.trim() ||
      typeof latest.checksum !== 'string' || !latest.checksum.trim() ||
      latest.manifest || !isAllowedDownloadUrl(latest.url, origin)) return;

  const { bundles } = await CapacitorUpdater.list();
  const matches = bundles.filter((bundle) => bundle.version === latest.version);
  if (matches.some((bundle) => bundle.status === 'error')) return;
  const staged = readRecord(PENDING_KEY);
  // Only reuse a local bundle whose provenance is bound to this exact binary.
  const existing = sameIdentity(staged, identity) && staged.version === latest.version && matches.find((bundle) =>
    bundle.id === staged.id && INSTALLABLE_STATUSES.has(bundle.status));
  const downloaded = existing || await CapacitorUpdater.download({
    url: latest.url,
    version: latest.version,
    checksum: latest.checksum,
    sessionKey: latest.sessionKey,
  });
  if (!downloaded.id || downloaded.version !== latest.version || !INSTALLABLE_STATUSES.has(downloaded.status)) return;
  // This marker is deliberately separate from the plugin's next() queue.
  const saved = { id: downloaded.id, version: downloaded.version, ...identity };
  window.localStorage.setItem(PENDING_KEY, JSON.stringify(saved));
  // If the startup loader has already been released, the user is using the
  // app: never replace the page under them. preparePendingBundle() installs
  // the saved bundle before rendering on the next launch.
  if (gateReleased) return;
  // While the loader still covers the UI, set() reloads the WebView so the new
  // page bundle is shown in this launch. Record the attempt first: if native
  // rolls the bundle back (and auto-deletes it), the same version must not be
  // downloaded and switched again on every launch.
  switching = true;
  window.localStorage.setItem(ATTEMPTED_KEY, JSON.stringify(saved));
  await CapacitorUpdater.set({ id: downloaded.id });
}
